import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { invitationsAPI } from '../../services/api';
import { getRoleLabel, getInvitationSignupSubtitle } from '../../lib/customerRoles';
import { parseInvitationSearchParams, persistInvitationParams } from '../../lib/invitationParams';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Building,
  Phone,
  Globe,
  Briefcase,
  MapPin,
} from 'lucide-react';
import toast from 'react-hot-toast';

function readInvitationFromWindow() {
  if (typeof window === 'undefined') {
    return { invitationToken: '', email: '', role: '' };
  }
  return parseInvitationSearchParams(window.location.search);
}

const Signup = () => {
  const [searchParams] = useSearchParams();
  const [urlInvite] = useState(readInvitationFromWindow);

  const [invitationToken, setInvitationToken] = useState(urlInvite.invitationToken);
  const [lockedEmail, setLockedEmail] = useState(urlInvite.email);
  const [lockedRole, setLockedRole] = useState(urlInvite.role);
  const [invitationMeta, setInvitationMeta] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(Boolean(urlInvite.invitationToken));
  const [invitationError, setInvitationError] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { signup } = useAuth();

  const isInvitedSignup = Boolean(invitationToken || lockedRole);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: urlInvite.email,
      role: urlInvite.role,
    },
  });

  const selectedRole = lockedRole || watch('role');
  const isOrgOwner = selectedRole === 'ORG_ADMIN';
  const orgFieldsLocked = Boolean(invitationMeta?.organizationPreConfigured);

  useEffect(() => {
    const params = parseInvitationSearchParams(`?${searchParams.toString()}`);
    const fromWindow = readInvitationFromWindow();

    const token = params.invitationToken || fromWindow.invitationToken;
    const email = params.email || fromWindow.email;
    const role = params.role || fromWindow.role;

    if (token) {
      setInvitationToken(token);
      persistInvitationParams({ invitationToken: token, email, role });
    }
    if (email) {
      setLockedEmail(email);
      setValue('email', email);
    }
    if (role) {
      setLockedRole(role);
      setValue('role', role);
    }
  }, [searchParams, setValue]);

  useEffect(() => {
    if (!invitationToken) return;

    let cancelled = false;
    const bootstrapEmail = lockedEmail;
    const bootstrapRole = lockedRole;

    const loadInvitation = async () => {
      try {
        setLoadingInvitation(true);
        setInvitationError(null);
        const response = await invitationsAPI.getByToken(invitationToken);
        if (cancelled) return;

        if (response.data?.success) {
          const data = response.data.data;
          setInvitationMeta(data);
          setLockedEmail(data.email);
          setLockedRole(data.role);
          setValue('email', data.email);
          setValue('role', data.role);
          if (data.organizationPreConfigured) {
            if (data.organizationName) setValue('organizationName', data.organizationName);
            if (data.organizationAddress) setValue('organizationAddress', data.organizationAddress);
            if (data.organizationWebsite) setValue('organizationWebsite', data.organizationWebsite);
            if (data.organizationPhone) setValue('organizationPhone', data.organizationPhone);
          }
        } else {
          setInvitationError('Invalid or expired invitation link.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching invitation:', error);
        if (!bootstrapEmail && !bootstrapRole) {
          setInvitationError(
            error.response?.data?.error || 'Invalid or expired invitation link.'
          );
        }
      } finally {
        if (!cancelled) setLoadingInvitation(false);
      }
    };

    loadInvitation();
    return () => {
      cancelled = true;
    };
  }, [invitationToken, setValue]);

  const onSubmit = async (data) => {
    if (invitationToken && !lockedEmail && !lockedRole) {
      toast.error('Invitation could not be loaded. Please use the link from your email.');
      return;
    }

    setIsSubmitting(true);
    try {
      const signupData = {
        ...data,
        email: lockedEmail || data.email,
        role: lockedRole || data.role,
        invitationToken: invitationToken || undefined,
      };

      const result = await signup(signupData);
      if (!result.success) {
        console.error('Signup error:', result.error);
      }
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingInvitation && invitationToken && !lockedEmail && !lockedRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (invitationError && invitationToken && !lockedEmail && !lockedRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invitation unavailable</h1>
          <p className="text-gray-600 mb-6">{invitationError}</p>
          <Link to="/login" className="btn-primary inline-block">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  const subtitleSource = invitationMeta || (lockedRole ? { role: lockedRole, organizationName: invitationMeta?.organizationName } : null);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex items-center justify-center mb-4">
            <img src="/logo.svg" alt="SNOWBOTIX Logo" className="h-16 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {isInvitedSignup ? 'Accept Invitation' : 'Create your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {isInvitedSignup && subtitleSource ? (
              getInvitationSignupSubtitle(subtitleSource)
            ) : (
              <>
                Or{' '}
                <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                  sign in to your existing account
                </Link>
              </>
            )}
          </p>
        </div>

        {invitationMeta && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3">
            {invitationMeta.organizationName && (
              <div className="flex items-start gap-3">
                <Building className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Organization</p>
                  <p className="font-medium text-gray-900">{invitationMeta.organizationName}</p>
                  {invitationMeta.organizationAddress && (
                    <p className="text-sm text-gray-600 mt-0.5">{invitationMeta.organizationAddress}</p>
                  )}
                </div>
              </div>
            )}
            {invitationMeta.invitedByName && (
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Invited by</p>
                  <p className="font-medium text-gray-900">{invitationMeta.invitedByName}</p>
                </div>
              </div>
            )}
            {invitationMeta.role === 'FIELD_OPERATOR' && invitationMeta.assignedSites?.length > 0 && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Assigned sites</p>
                  <p className="font-medium text-gray-900">{invitationMeta.assignedSites.join(', ')}</p>
                </div>
              </div>
            )}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="form-label">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="name"
                  type="text"
                  autoComplete="name"
                  className={`input pl-10 ${errors.name ? 'border-danger-500' : ''}`}
                  placeholder="Enter your full name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: { value: 2, message: 'Name must be at least 2 characters long' },
                    maxLength: { value: 50, message: 'Name cannot exceed 50 characters' },
                  })}
                />
              </div>
              {errors.name && <p className="form-error">{errors.name.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                {lockedEmail ? (
                  <input
                    id="email"
                    type="email"
                    readOnly
                    value={lockedEmail}
                    className="input pl-10 bg-gray-100 text-gray-900 cursor-not-allowed"
                  />
                ) : (
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`input pl-10 ${errors.email ? 'border-danger-500' : ''}`}
                    placeholder="Enter your email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                )}
              </div>
              {lockedEmail && (
                <input type="hidden" {...register('email', { required: 'Email is required' })} />
              )}
              {!lockedEmail && errors.email && <p className="form-error">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="role" className="form-label">
                Role
              </label>
              {lockedRole ? (
                <>
                  <select
                    id="role"
                    disabled
                    value={lockedRole}
                    className="input bg-gray-100 text-gray-900 cursor-not-allowed"
                  >
                    <option value={lockedRole}>{getRoleLabel(lockedRole)}</option>
                  </select>
                  <input type="hidden" {...register('role', { required: 'Role is required' })} />
                  <p className="text-sm text-gray-500 mt-1">Role assigned by your organization admin</p>
                </>
              ) : (
                <>
                  <select
                    id="role"
                    className={`input ${errors.role ? 'border-danger-500' : ''}`}
                    {...register('role', { required: 'Role is required' })}
                  >
                    <option value="">Select a role</option>
                    <option value="SUPERVISOR">Supervisor</option>
                    <option value="ORG_ADMIN">Organization Admin (invite only)</option>
                  </select>
                  {errors.role && <p className="form-error">{errors.role.message}</p>}
                </>
              )}
            </div>

            {isOrgOwner && (
              <>
                {orgFieldsLocked && (
                  <p className="text-sm text-gray-600 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">
                    Organization details were configured by Snowbotix during setup. Confirm your account below.
                  </p>
                )}
                <div>
                  <label htmlFor="organizationName" className="form-label">
                    Organization Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    {orgFieldsLocked ? (
                      <>
                        <input
                          id="organizationName"
                          type="text"
                          readOnly
                          value={invitationMeta?.organizationName || ''}
                          className="input pl-10 bg-gray-100 text-gray-900 cursor-not-allowed"
                        />
                        <input
                          type="hidden"
                          {...register('organizationName', { required: 'Organization name is required' })}
                        />
                      </>
                    ) : (
                      <input
                        id="organizationName"
                        type="text"
                        className={`input pl-10 ${errors.organizationName ? 'border-danger-500' : ''}`}
                        placeholder="Enter organization name"
                        {...register('organizationName', {
                          required: 'Organization name is required',
                          minLength: { value: 2, message: 'Organization name must be at least 2 characters' },
                          maxLength: { value: 100, message: 'Organization name cannot exceed 100 characters' },
                        })}
                      />
                    )}
                  </div>
                  {!orgFieldsLocked && errors.organizationName && (
                    <p className="form-error">{errors.organizationName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organizationAddress" className="form-label">
                    Organization Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Building className="h-5 w-5 text-gray-400" />
                    </div>
                    {orgFieldsLocked ? (
                      <input
                        id="organizationAddress"
                        type="text"
                        readOnly
                        value={invitationMeta?.organizationAddress || ''}
                        className="input pl-10 bg-gray-100 text-gray-900 cursor-not-allowed"
                      />
                    ) : (
                      <input
                        id="organizationAddress"
                        type="text"
                        className={`input pl-10 ${errors.organizationAddress ? 'border-danger-500' : ''}`}
                        placeholder="Enter organization address"
                        {...register('organizationAddress', {
                          maxLength: { value: 255, message: 'Organization address cannot exceed 255 characters' },
                        })}
                      />
                    )}
                  </div>
                  {!orgFieldsLocked && errors.organizationAddress && (
                    <p className="form-error">{errors.organizationAddress.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organizationWebsite" className="form-label">
                    Organization Website
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Globe className="h-5 w-5 text-gray-400" />
                    </div>
                    {orgFieldsLocked ? (
                      <input
                        id="organizationWebsite"
                        type="url"
                        readOnly
                        value={invitationMeta?.organizationWebsite || ''}
                        className="input pl-10 bg-gray-100 text-gray-900 cursor-not-allowed"
                      />
                    ) : (
                      <input
                        id="organizationWebsite"
                        type="url"
                        className={`input pl-10 ${errors.organizationWebsite ? 'border-danger-500' : ''}`}
                        placeholder="https://example.com"
                        {...register('organizationWebsite', {
                          maxLength: { value: 255, message: 'Organization website cannot exceed 255 characters' },
                        })}
                      />
                    )}
                  </div>
                  {!orgFieldsLocked && errors.organizationWebsite && (
                    <p className="form-error">{errors.organizationWebsite.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organizationPhone" className="form-label">
                    Organization Phone
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    {orgFieldsLocked ? (
                      <input
                        id="organizationPhone"
                        type="tel"
                        readOnly
                        value={invitationMeta?.organizationPhone || ''}
                        className="input pl-10 bg-gray-100 text-gray-900 cursor-not-allowed"
                      />
                    ) : (
                      <input
                        id="organizationPhone"
                        type="tel"
                        className={`input pl-10 ${errors.organizationPhone ? 'border-danger-500' : ''}`}
                        placeholder="Enter phone number"
                        {...register('organizationPhone', {
                          maxLength: { value: 30, message: 'Organization phone cannot exceed 30 characters' },
                        })}
                      />
                    )}
                  </div>
                  {!orgFieldsLocked && errors.organizationPhone && (
                    <p className="form-error">{errors.organizationPhone.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="organizationPosition" className="form-label">
                    Your Position in Organization
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Briefcase className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="organizationPosition"
                      type="text"
                      className={`input pl-10 ${errors.organizationPosition ? 'border-danger-500' : ''}`}
                      placeholder="e.g. Operations Manager"
                      {...register('organizationPosition', {
                        maxLength: { value: 100, message: 'Position cannot exceed 100 characters' },
                      })}
                    />
                  </div>
                  {errors.organizationPosition && (
                    <p className="form-error">{errors.organizationPosition.message}</p>
                  )}
                </div>
              </>
            )}

            <div>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input pl-10 pr-10 ${errors.password ? 'border-danger-500' : ''}`}
                  placeholder="Create a password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters long' },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                      message: 'Password must contain at least one lowercase letter, one uppercase letter, and one number',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && <p className="form-error">{errors.password.message}</p>}
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="acceptTerms"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  {...register('acceptTerms', {
                    required: 'You must accept the terms and conditions to create an account',
                  })}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="acceptTerms" className="font-medium text-gray-700">
                  I acknowledge and accept the terms and conditions
                </label>
                <p className="text-gray-500 mt-1">
                  By creating an account, you agree to our terms of service and privacy policy.
                </p>
              </div>
            </div>
            {errors.acceptTerms && <p className="form-error">{errors.acceptTerms.message}</p>}
          </div>

          <div>
            <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Creating account...
                </div>
              ) : isInvitedSignup ? (
                'Accept invitation & create account'
              ) : (
                'Create account'
              )}
            </button>
          </div>

          <div className="text-center">
            <Link to="/login" className="text-sm text-primary-600 hover:text-primary-500">
              Already have an account? Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
